import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Loader2,
  Pencil,
  Plus,
  RefreshCcw,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  deleteRole,
  listMembers,
  listPermissionsCatalog,
  listRoles,
} from "@/api/projects";
import type {
  PermissionCatalogCategory,
  ProjectMember,
  Role,
} from "@/api/types";
import { RoleEditDialog } from "./RoleEditDialog";

interface RolesSectionProps {
  projectSlug: string;
}

export function RolesSection({ projectSlug }: RolesSectionProps) {
  const [roles, setRoles] = useState<Role[]>([]);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editTarget, setEditTarget] = useState<Role | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [r, m, p] = await Promise.all([
        listRoles(projectSlug),
        listMembers(projectSlug),
        listPermissionsCatalog(projectSlug).catch(
          () => [] as PermissionCatalogCategory[],
        ),
      ]);
      setRoles(r);
      setMembers(m);
      setCatalog(p);
    } catch (err) {
      setErrorMessage(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  const memberCountByRole = useMemo(() => {
    const m = new Map<number, number>();
    for (const member of members) {
      m.set(member.role.id, (m.get(member.role.id) ?? 0) + 1);
    }
    return m;
  }, [members]);

  const sortedRoles = useMemo(
    () =>
      roles.slice().sort((a, b) => {
        if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [roles],
  );

  function applyRoleUpdate(role: Role) {
    setRoles((prev) => {
      const exists = prev.some((r) => r.id === role.id);
      if (exists) return prev.map((r) => (r.id === role.id ? role : r));
      return [...prev, role];
    });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRole(projectSlug, deleteTarget.id);
      setRoles((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="space-y-5">
      <header className="flex items-end justify-between">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-slate-900">
            Roles
          </h2>
          <p className="text-sm text-slate-500">
            Define what each role is allowed to do in this project.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
          >
            <RefreshCcw
              className={
                loading
                  ? "mr-2 h-3.5 w-3.5 animate-spin"
                  : "mr-2 h-3.5 w-3.5"
              }
            />
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setCreateOpen(true)}
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            New role
          </Button>
        </div>
      </header>

      <div className="rounded-plane bg-white shadow-plane">
        {loading ? (
          <div className="flex items-center justify-center px-5 py-10 text-sm text-slate-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading roles…
          </div>
        ) : errorMessage ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm font-medium text-rose-700">
              Couldn&rsquo;t load roles
            </p>
            <p className="mt-1 text-xs text-rose-600">{errorMessage}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="mt-3"
            >
              Retry
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-900/[0.06]">
            {sortedRoles.map((r) => {
              const count = memberCountByRole.get(r.id) ?? 0;
              return (
                <li
                  key={r.id}
                  className="flex items-start gap-4 px-5 py-4"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">
                        {r.name}
                      </span>
                      {r.is_system && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-indigo-700">
                          <ShieldCheck className="h-3 w-3" />
                          System
                        </span>
                      )}
                      {r.is_default && (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-600">
                          Default
                        </span>
                      )}
                    </div>
                    {r.description && (
                      <p className="mt-1 text-xs text-slate-500">
                        {r.description}
                      </p>
                    )}
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-400">
                      <span>
                        {count} {count === 1 ? "member" : "members"}
                      </span>
                      <span>·</span>
                      <span>
                        {r.permissions.length}{" "}
                        {r.permissions.length === 1
                          ? "permission"
                          : "permissions"}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditTarget(r)}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit
                    </Button>
                    {!r.is_system && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(r)}
                        className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <RoleEditDialog
        projectSlug={projectSlug}
        open={!!editTarget}
        role={editTarget}
        catalog={catalog}
        onOpenChange={(o) => {
          if (!o) setEditTarget(null);
        }}
        onSaved={(saved) => {
          applyRoleUpdate(saved);
          setEditTarget(null);
        }}
      />

      <RoleEditDialog
        projectSlug={projectSlug}
        open={createOpen}
        role={null}
        catalog={catalog}
        onOpenChange={setCreateOpen}
        onSaved={(saved) => {
          applyRoleUpdate(saved);
          setCreateOpen(false);
        }}
      />

      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (deleting) return;
          if (!o) setDeleteTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete role?</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>
                  This will remove{" "}
                  <span className="font-medium text-slate-900">
                    {deleteTarget.name}
                  </span>
                  .{" "}
                  {(memberCountByRole.get(deleteTarget.id) ?? 0) > 0 ? (
                    <>
                      The {memberCountByRole.get(deleteTarget.id)} member
                      {memberCountByRole.get(deleteTarget.id) === 1 ? "" : "s"}{" "}
                      currently assigned to this role will need to be
                      reassigned.
                    </>
                  ) : (
                    "No member is currently assigned to this role."
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
            <p className="text-xs leading-relaxed text-rose-700">
              This action is irreversible. The role will be permanently
              deleted.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
